import {useCallback, useMemo} from 'react';
import {Concept} from 'types';
import type {Agent, AgentWorkSummary} from 'services/agent/types';
import type {WorkHit} from 'services/elastic/types';
import {useLocalization} from 'localization';
import {useCodelists} from 'services/codelists';
import {useSearchAgentWorksQuery} from 'services/elastic/elasticApi';
import {
  mapWorkHitToWorkSummary,
  useMetadataCodelistIds,
} from 'services/metadata';

const schemaFieldToElasticField: Partial<
  Record<keyof AgentWorkSummary, string>
> = {
  id: 'id',
  type: 'type',
  created: 'created',
  modified: 'modified',
  preferredTitle: 'preferredTitles.keyword',
  languages: 'languageCodes.keyword',
  intellectualLevel: 'intellectualLevelCodes.keyword',
  literatureType: 'literatureTypeCodes.keyword',
  dewey: 'dewey.value',
  themes: 'themeCodes.keyword',
  themaCodes: 'themeCodes.keyword',
  themaLabels: 'themeCodes.keyword',
  subjects: 'subjectCodes.keyword',
  educationLevel: 'educationLevelCodes.keyword',
  educationTargetGroup: 'educationTargetGroupCodes.keyword',
  grep: 'grepCodes.keyword',
  genreAndForm: 'genreAndFormCodes.keyword',
  bibleContent: 'bibleContentCodes.keyword',
  bibleVersion: 'bibleVersionCode.keyword',
  generalNote: 'generalNote',
  norwegianForAdultImmigrants: 'norwegianForAdultImmigrants',
  useTitleAsMainEntry: 'useTitleAsMainEntry',
  firstPublishedYear: 'firstPublishedYear',
  // Nested paths (dewey, expressions.manifestations) get a nested sort context in mapAgentWorksSearchPropsToSearchProps.
  mainTitles: 'expressions.manifestations.titles.mainTitles.value.keyword',
  publishedYear: 'expressions.manifestations.publishedYear',
};

const mapSchemaFieldToElasticField = (
  schemaField?: keyof AgentWorkSummary,
): string | undefined =>
  schemaField ? schemaFieldToElasticField[schemaField] : undefined;

export const useAgentWorks = (
  agent: Pick<Agent, 'id' | 'roles' | 'agentType'> | undefined,
  page: number,
  size: number,
  sortField?: string,
  sortOrder?: 'asc' | 'desc',
  doLoad?: boolean,
): {isLoading: boolean; data: AgentWorkSummary[]; total: number} => {
  const {t} = useLocalization();
  const codelistIds = useMetadataCodelistIds();
  const codelists = useCodelists(codelistIds);

  const {data, isFetching} = useSearchAgentWorksQuery(
    {
      agentId: agent!.id!,
      page,
      size,
      sortField: sortField
        ? mapSchemaFieldToElasticField(sortField as keyof AgentWorkSummary)
        : undefined,
      sortOrder,
    },
    {
      skip: !doLoad || !agent || !agent?.id,
    },
  );

  const mapWorkHitsToAgentWorkSummaries = useCallback(
    (
      workHits: WorkHit[],
      agent: Pick<Agent, 'roles' | 'id' | 'agentType'>,
    ): AgentWorkSummary[] => {
      return workHits.map(h => ({
        ...mapWorkHitToWorkSummary(h, codelists),
        roles:
          agent.agentType === Concept.publisher
            ? [t(`general.entity.${Concept.publisher}`)]
            : (agent.roles?.find(r => r.workId === h.id)?.roles ?? []),
      }));
    },
    [codelists, t],
  );

  return useMemo(() => {
    return {
      total: data?.total ?? 0,
      isLoading: isFetching,
      data:
        agent && data && data?.hits?.length > 0
          ? mapWorkHitsToAgentWorkSummaries(data.hits, agent)
          : [],
    };
  }, [agent, data, isFetching, mapWorkHitsToAgentWorkSummaries]);
};
